import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Grid,
  Button,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Radio,
  FormControlLabel,
  RadioGroup,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  FormLabel,
} from "@mui/material";

import Color from "../../../theme/color";
import TexBoxWithLabel from "../../../components/text-field";
import { LoadingButton } from "@mui/lab";
import ValidationError from "../../../components/validation-error-label";
import {
  isEmpty,
  isFileSizeWithinLimit,
  isNumeric,
  isValidEmail,
  isValidFileExtension,
  isValidImageFormat,
} from "../../../utils/validator";
import FullScreenLoader from "../../../components/full-screen-loader";
import { GET, POST, PUT } from "../../../config/https";
import APIS from "../../../config/api";
import toast from "react-hot-toast";
import { Icon } from "@iconify/react";
import { GetLocalStorage } from "../../../utils/local-storage";
import { usePersistentState } from "../../../contexts/global-context";
import Iconify from "../../../components/iconify/Iconify";
import { IMaskInput } from "react-imask";
import { marginBottomSize } from "../../../utils/constant";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskCustom = React.forwardRef<HTMLInputElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="(#00) 000-0000"
        definitions={{
          "#": /[1-9]/,
        }}
        inputRef={ref}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);
interface ProfileData {
  firstName: string;
  lastName: string;
  address: string;
  phoneNumber: string;
  email: string;
  companyName: string;
  bankName: string;
  accountHolderName: string;
  routingNumber: string;
  accountNumber: string;
  conformAccountNumber: string;
  profileImage: string;
  zelleId: string;
  bankAccountType: string | number;
}
interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}
const accountTypes = [
  { id: 1, type: "Savings", label: "Savings" },
  { id: 2, type: "Checking", label: "Checking" },
];
const ProfileDataDefault: ProfileData = {
  firstName: "",
  lastName: "",
  address: "",
  phoneNumber: "",
  email: "",
  companyName: "",
  bankName: "",
  accountHolderName: "",
  accountNumber: "",
  conformAccountNumber: "",
  routingNumber: "",
  profileImage: "",
  zelleId: "",
  bankAccountType: "",
};
const PasswordDataDefault: PasswordData = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

export default function Profile() {
  const [state, dispatch]: any = usePersistentState();
  const [profileData, setProfileData] =
    useState<ProfileData>(ProfileDataDefault);
  const [passwordData, setPasswordData] =
    useState<PasswordData>(PasswordDataDefault);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [loader, setLoader] = useState(false);
  const [isValidation, setIsValidation] = useState(false);
  const [isPasswordValidation, setPasswordIsValidation] = useState(false);
  const [coverImages, setCoverImages] = useState("");
  const [open, setOpen] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>(
    {}
  );
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [, setValue] = React.useState(profileData?.bankAccountType);
  const [isGoogleLogin, setIsGoogleLogin] = useState(true);

  useEffect(() => {
    if (state?.Event && profileData) {
      const newData: any = { ...profileData };
      newData.conformAccountNumber = state?.Event?.accountNumber;
      setProfileData(newData);
    } else {
      const newData: any = { ...profileData };
      newData.conformAccountNumber = "";
      setProfileData(newData);
    }
  }, [state?.Event]);

  const validatePasswordFields = () => {
    const errors: Record<string, string> = {};
    if (isEmpty(passwordData?.currentPassword ?? "")) {
      errors.currentPassword = "Please enter Current Password";
    }
    if (isEmpty(passwordData?.newPassword ?? "")) {
      errors.newPassword = "Please enter New Password";
    }
    if (isEmpty(passwordData?.confirmPassword ?? "")) {
      errors.confirmPassword = "Please enter Confirm Password";
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateOnlyPasswordFields = () => {
    const errors: Record<string, string> = {};

    if (isEmpty(passwordData?.newPassword ?? "")) {
      errors.newPassword = "Please enter New Password";
    }
    if (isEmpty(passwordData?.confirmPassword ?? "")) {
      errors.confirmPassword = "Please enter Confirm Password";
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    const selectedId = accountTypes.find(
      (account) => account.type === event.target.value
    )?.id;
    const newData: any = { ...profileData };
    newData.bankAccountType = selectedId;
    setProfileData(newData);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickOpen = () => {
    resetPasswordData();
    setOpen(true);
  };

  useEffect(() => {
    if (isValidation) {
      validator();
    }
  }, [profileData, coverImages]);

  useEffect(() => {
    if (isPasswordValidation) {
      validatePasswordFields();
    }
  }, [passwordData]);

  useEffect(() => {
    GetUserData();
  }, []);
  const GetUserData = async () => {
    setLoader(true);
    let userData = GetLocalStorage("loginData");
    let params: any = {
      id: userData.id,
    };
    await GET(APIS.GET_USER_BY_ID, params)
      .then((res: any) => {
        if (res?.isSuccess) {
          dispatch({ Event: res.result });
          setProfileData({
            ...res?.result,
            bankAccountType: res?.result?.bankAccountType,
          });
          setIsGoogleLogin(res?.result?.isGoogleLogin);
          let newData: any = { coverImages };
          newData = res?.result?.profileImage;
          setCoverImages(newData);
        } else {
          console.log(toast.error(res?.errors[0]));
        }
      })
      .catch((error) => {
        console.log("error", error);
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const onChangeFormData = async (key: keyof ProfileData, e: any) => {
    let newData: any = { ...profileData };
    newData[key] = e.target.value;
    setProfileData(newData);
    validateField(key, e.target.value);
  };
  const onChangePasswordData = async (key: keyof PasswordData, e: any) => {
    let newData = { ...passwordData };
    newData[key] = e.target.value;
    setPasswordData(newData);
    validatePasswordFields();
  };
  let userData = GetLocalStorage("loginData");

  const resetPasswordData = () => {
    setPasswordData(PasswordDataDefault);
  };

  const handleChangeCoverImage = (event: any) => {
    let temp = event.target?.files[0];
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const base64: any = e.target.result;
      let newData = coverImages;
      newData = base64;
      setCoverImages(newData);
      setProfileData((prevData) => ({
        ...prevData,
        profileImage: base64,
      }));
    };
    reader.readAsDataURL(temp);
  };

  const handleClick = async () => {
    setIsValidation(true);
    if (!validator()) {
      return;
    }
    setLoader(true);

    let param = {
      id: userData?.id,
      firstName: profileData?.firstName,
      lastName: profileData?.lastName,
      email: profileData?.email,
      password: null,
      address: profileData?.address,
      profileImage: coverImages?.split(",")[1] || "",
      phoneNumber: profileData?.phoneNumber,
      accountHolderName: profileData?.accountHolderName,
      accountNumber: profileData?.accountNumber,
      routingNumber: profileData?.routingNumber,
      bankName: profileData?.bankName,
      companyName: profileData?.companyName,
      bankAccountType: profileData?.bankAccountType,
    };
    await PUT(APIS.UPDATE_USER, param)
      .then((res: any) => {
        if (res?.isSuccess) {
          localStorage.setItem(
            "coverImage",
            JSON.stringify(profileData?.profileImage)
          );
          resetPasswordData();
          toast.success("Submit successful");
        }
      })
      .catch((error) => {
        console.log("error", error);
      })
      .finally(() => {
        setLoader(false);
        setIsValidation(false);
        setPasswordIsValidation(false);
      });
    setLoader(false);
    setIsValidation(false);
    setPasswordIsValidation(false);
  };

  const handleChangePassword = async () => {
    if (!validatePasswordFields()) {
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordErrors({ confirmPassword: "Passwords do not match" });
      return;
    }

    let firstApiSuccess = false;
    let currentPassword = {
      email: profileData?.email,
      password: passwordData.currentPassword,
    };

    await POST(APIS.VALIDATE_PASSWORD, currentPassword)
      .then((res: any) => {
        if (res?.isSuccess) {
          firstApiSuccess = true;
        } else {
          setPasswordErrors({ currentPassword: res?.errors[0] });
        }
      })
      .catch((error) => {
        console.log("error", error);
        setPasswordErrors({ currentPassword: "An error occurred" });
      })
      .finally(() => {
        setLoader(false);
        setIsValidation(false);
        setPasswordIsValidation(false);
      });

    if (firstApiSuccess) {
      let newPassword = {
        email: profileData?.email,
        newPassword: passwordData.newPassword,
      };
      await POST(APIS.CHANGE_USER_PASSWORD, newPassword)
        .then((res: any) => {
          if (res?.isSuccess) {
            setOpen(false);
            resetPasswordData();
            toast.success("Password changed successfully");
          } else {
            setPasswordErrors({ confirmPassword: res?.errors[0] });
          }
        })
        .catch((error) => {
          console.log("error", error);
          setPasswordErrors({ confirmPassword: "An error occurred" });
        })
        .finally(() => {
          setLoader(false);
          setIsValidation(false);
          setPasswordIsValidation(false);
        });
    }
  };

  const handleSavePassword = async () => {
    if (!validateOnlyPasswordFields()) {
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordErrors({ confirmPassword: "Passwords do not match" });
      return;
    }
    let newPassword = {
      email: profileData?.email,
      newPassword: passwordData.newPassword,
    };
    await POST(APIS.CHANGE_USER_PASSWORD, newPassword)
      .then((res: any) => {
        if (res?.isSuccess) {
          setOpen(false);
          toast.success("Password changed successfully");
        } else {
          setPasswordErrors({ confirmPassword: res?.errors[0] });
        }
      })
      .catch((error) => {
        console.log("error", error);
        setPasswordErrors({ confirmPassword: "An error occurred" });
      })
      .finally(() => {
        setLoader(false);
        setIsValidation(false);
        setPasswordIsValidation(false);
      });
  };

  const validator = () => {
    const errors: Record<string, string> = {};
    if (
      profileData &&
      profileData.accountNumber !== profileData.conformAccountNumber
    ) {
      errors.conformAccountNumber = "Account numbers do not match";
    }
    if (profileData && isEmpty(profileData?.firstName ?? "")) {
      errors.firstName = "Please enter  First Name";
    }
    if (profileData && isEmpty(profileData?.lastName ?? "")) {
      errors.lastName = "Please enter  Last Name";
    }
    if (profileData && isEmpty(profileData?.phoneNumber ?? "")) {
      errors.phoneNumber = "Please enter a phone number";
    } else if (profileData?.phoneNumber.replace(/\D/g, "").length < 10) {
      errors.phoneNumber = "Phone number must be at least 10 digits";
    } else {
      delete errors.phoneNumber;
    }
    if (profileData && isEmpty(profileData?.email ?? "")) {
      errors.email = "Please enter Email";
    }
    if (profileData && isEmpty(profileData?.companyName ?? "")) {
      errors.companyName = "Please enter Company Name";
    }
    if (!isEmpty(profileData?.email) && !isValidEmail(profileData?.email)) {
      errors.email = "Please enter a valid email";
    }

    if (coverImages) {
      if (typeof coverImages === "string") {
        if (!isValidImageFormat(coverImages)) {
          errors.coverImage = "Invalid Image Format";
        } else if (!isValidFileExtension(coverImages)) {
          errors.coverImage =
            "Invalid File Extension ,Please select a valid JPEG, JPG, or PNG file.";
        } else if (!isFileSizeWithinLimit(coverImages)) {
          errors.coverImage =
            "Invalid Image Size. Image size should be less than 2 MB.";
        }
      } else {
        if (!isValidImageFormat(coverImages)) {
          errors.coverImage = "Invalid Image Format";
        } else if (!isValidFileExtension(coverImages)) {
          errors.coverImage = "Invalid File Extension";
        } else if (!isFileSizeWithinLimit(coverImages)) {
          errors.coverImage =
            "Invalid Image Size. Image size should be less than 2 MB.";
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateField = (fieldName: keyof ProfileData, value: string) => {
    const errors: Record<string, string> = { ...formErrors };
    if (fieldName === "email") {
      if (isEmpty(value)) {
        errors.email = "Please enter an email";
      } else if (!isValidEmail(value)) {
        errors.email = "Please enter a valid email";
      } else {
        delete errors.email;
      }
    }
    if (fieldName === "phoneNumber") {
      if (isEmpty(value)) {
        errors.phoneNumber = "Please enter phone number";
      } else if (!isNumeric(value)) {
        errors.phoneNumber = "Please enter a valid phone number";
      } else {
        delete errors.phoneNumber;
      }
    }

    if (value.trim() !== "") {
      delete errors[fieldName];
    }
    setFormErrors(errors);
  };

  const handleButtonClick = () => {
    const fileInput = document.getElementById(
      "file-upload"
    ) as HTMLInputElement;
    fileInput.click();
  };

  return (
    <>
      <Helmet>
        <title>Profile | EventPrix</title>
      </Helmet>
      <FullScreenLoader open={loader} />
      <Container maxWidth="xl" style={{ marginBottom: marginBottomSize }}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={3}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                marginTop: "80px",
              }}
            >
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="file-upload"
                type="file"
                onChange={handleChangeCoverImage}
              />
              <label
                htmlFor="file-upload"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <div
                  className="circle"
                  style={{
                    width: "160px",
                    height: "160px",
                    borderRadius: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  {profileData.profileImage ? (
                    <img
                      src={profileData.profileImage}
                      style={{
                        borderRadius: "100px",
                        height: "160px",
                        width: "160px",
                      }}
                      alt="Profile"
                      className="profile-pic"
                    />
                  ) : (
                    <Icon
                      icon="iconamoon:profile-circle-fill"
                      style={{
                        borderRadius: "100px",
                        height: "160px",
                        width: "160px",
                      }}
                      color="#777"
                    />
                  )}
                </div>
              </label>
              <Button
                component="span"
                variant="outlined"
                color="primary"
                onClick={handleButtonClick}
                style={{
                  marginTop: "10px",
                  borderRadius: "100px",
                  color: "black",
                  border: "1px solid #e6ac74",
                }}
              >
                Upload Image
              </Button>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div>
                <ValidationError formErrors={formErrors.coverImage} />
              </div>
            </div>
          </Grid>

          <Grid item xs={12} sm={9}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                margin: "10px 0 0 0",
              }}
            >
              <Icon
                width="28"
                height="28"
                color={Color.secondary}
                style={{ marginRight: 5 }}
                icon="healthicons:ui-user-profile"
              />
              <Typography
                style={{
                  color: Color.secondary,
                }}
                variant="h6"
              >
                Profile Detail
              </Typography>
            </div>
            <Divider
              style={{ margin: "5px 0 30px 0" }}
              orientation="horizontal"
            />
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TexBoxWithLabel
                  multiline={true}
                  size="small"
                  placeholder="First Name"
                  label="First Name *"
                  value={profileData.firstName}
                  onChange={(firstName) =>
                    onChangeFormData("firstName", firstName)
                  }
                />
                <ValidationError formErrors={formErrors.firstName} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TexBoxWithLabel
                  size="small"
                  multiline={true}
                  placeholder="Last Name"
                  label="Last Name *"
                  value={profileData.lastName}
                  onChange={(lastName) =>
                    onChangeFormData("lastName", lastName)
                  }
                />
                <ValidationError formErrors={formErrors.lastName} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TexBoxWithLabel
                  size="small"
                  placeholder="Email"
                  multiline={true}
                  label="Email *"
                  value={profileData.email}
                  onChange={(email) => onChangeFormData("email", email)}
                />
                <ValidationError formErrors={formErrors.email} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  placeholder="(000) 000-0000"
                  label="Phone Number *"
                  fullWidth
                  variant="outlined"
                  value={profileData.phoneNumber}
                  onChange={(phoneNumber) =>
                    onChangeFormData("phoneNumber", phoneNumber)
                  }
                  name="PhoneNumber"
                  id="formatted-text-mask-input"
                  InputProps={{
                    inputComponent: TextMaskCustom as any,
                  }}
                />
                <ValidationError formErrors={formErrors.phoneNumber} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TexBoxWithLabel
                  size="small"
                  multiline={true}
                  placeholder="Company / Organizer "
                  label="Company / Organizer *"
                  value={profileData.companyName}
                  onChange={(companyName) =>
                    onChangeFormData("companyName", companyName)
                  }
                />
                <ValidationError formErrors={formErrors.companyName} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TexBoxWithLabel
                  size="small"
                  multiline={true}
                  placeholder="Address"
                  label="Address"
                  value={profileData.address}
                  onChange={(address) => onChangeFormData("address", address)}
                />
              </Grid>
            </Grid>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                margin: "35px 0 0 0",
              }}
            >
              <Icon
                width="25"
                color={Color.secondary}
                height="25"
                style={{ marginRight: 5 }}
                icon="mingcute:bank-line"
              />
              <Typography
                style={{
                  color: Color.secondary,
                }}
                variant="h6"
              >
                Bank Detail
              </Typography>
            </div>
            <Divider
              style={{ margin: "5px 0 30px 0" }}
              orientation="horizontal"
            />
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TexBoxWithLabel
                  size="small"
                  multiline={true}
                  label="Bank Name "
                  placeholder="Bank Name"
                  value={profileData.bankName}
                  onChange={(bankName) =>
                    onChangeFormData("bankName", bankName)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TexBoxWithLabel
                  multiline={true}
                  size="small"
                  label="Account Holder Name "
                  placeholder="Account Holder Name"
                  value={profileData.accountHolderName}
                  onChange={(accountHolderName) =>
                    onChangeFormData("accountHolderName", accountHolderName)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Bank Account Type</FormLabel>
                  <RadioGroup
                    row
                    aria-label="bank-account-type"
                    name="bank-account-type"
                    value={profileData.bankAccountType}
                    onChange={(e) => {
                      const selectedValue = parseInt(e.target.value);
                      setProfileData((prevData) => ({
                        ...prevData,
                        bankAccountType: selectedValue,
                      }));
                    }}
                  >
                    <FormControlLabel
                      value={1}
                      control={<Radio />}
                      label="Saving"
                    />
                    <FormControlLabel
                      value={2}
                      control={<Radio />}
                      label="Current"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TexBoxWithLabel
                  multiline={true}
                  size="small"
                  label="Routing number "
                  placeholder="Routing number"
                  value={profileData.routingNumber}
                  onChange={(routingNumber) =>
                    onChangeFormData("routingNumber", routingNumber)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  style={{ width: "100%" }}
                  label="Account Number "
                  placeholder="Account Number "
                  value={profileData.accountNumber}
                  onChange={(accountNumber) =>
                    onChangeFormData("accountNumber", accountNumber)
                  }
                  type={showAccountNumber ? "text" : "password"}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowAccountNumber(!showAccountNumber)
                          }
                          edge="end"
                        >
                          <Iconify
                            icon={
                              showAccountNumber
                                ? "eva:eye-fill"
                                : "eva:eye-off-fill"
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <ValidationError formErrors={formErrors.accountNumber} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TexBoxWithLabel
                  size="small"
                  label="Conform Account Number "
                  placeholder="Conform Account Number"
                  value={profileData.conformAccountNumber}
                  onChange={(conformAccountNumber) =>
                    onChangeFormData(
                      "conformAccountNumber",
                      conformAccountNumber
                    )
                  }
                />
                <ValidationError formErrors={formErrors.conformAccountNumber} />
              </Grid>
            </Grid>

            <Divider style={{ margin: "15px" }} orientation="horizontal">
              OR
            </Divider>

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TexBoxWithLabel
                  size="small"
                  label="Zelle id "
                  placeholder="Zelle id"
                  value={profileData.zelleId}
                  onChange={(zelleId) => onChangeFormData("zelleId", zelleId)}
                />
              </Grid>
            </Grid>
            <Typography
              style={{
                color: Color.color14,
                margin: "40px 10px 20px 0",
              }}
              variant="h5"
            >
              Password
            </Typography>
            <a href="#" onClick={handleClickOpen}>
              Change Password
            </a>
            {isGoogleLogin ? (
              <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
              >
                <DialogTitle id="form-dialog-title">
                  Change Password
                </DialogTitle>
                <DialogContent>
                  <Grid
                    style={{ marginTop: "5px" }}
                    container
                    spacing={2}
                    alignItems="center"
                  >
                    <Grid item xs={12} sm={12}>
                      <TextField
                        size="small"
                        style={{ width: "100%" }}
                        label="New Password *"
                        value={passwordData.newPassword}
                        onChange={(newPassword) =>
                          onChangePasswordData("newPassword", newPassword)
                        }
                        type={showPassword2 ? "text" : "password"}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword2(!showPassword2)}
                                edge="end"
                              >
                                <Iconify
                                  icon={
                                    showPassword2
                                      ? "eva:eye-fill"
                                      : "eva:eye-off-fill"
                                  }
                                />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <ValidationError
                        formErrors={passwordErrors.newPassword}
                      />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                      <TexBoxWithLabel
                        size="small"
                        placeholder="Confirm New Password"
                        multiline={true}
                        label="Confirm New Password *"
                        value={passwordData.confirmPassword}
                        onChange={(confirmPassword) =>
                          onChangePasswordData(
                            "confirmPassword",
                            confirmPassword
                          )
                        }
                      />

                      <ValidationError
                        formErrors={passwordErrors.confirmPassword}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <div style={{ padding: "0 18px 0 0", marginBottom: "15px" }}>
                    <Button
                      variant="contained"
                      onClick={handleClose}
                      style={{
                        backgroundColor: Color.primary,
                        marginRight: "10px",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSavePassword}
                      variant="contained"
                      style={{ backgroundColor: Color.primary }}
                    >
                      Change Password
                    </Button>
                  </div>
                </DialogActions>
              </Dialog>
            ) : (
              <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="form-dialog-title"
              >
                <DialogTitle id="form-dialog-title">
                  Change Password
                </DialogTitle>
                <DialogContent>
                  <Grid
                    style={{ marginTop: "10px" }}
                    container
                    spacing={4}
                    alignItems="center"
                  >
                    <Grid
                      style={{ padding: "0 0 0 32px " }}
                      item
                      xs={12}
                      sm={12}
                    >
                      <TextField
                        size="small"
                        placeholder="Current Password"
                        style={{ width: "100%" }}
                        label="Current Password"
                        value={passwordData.currentPassword}
                        onChange={(currentPassword) =>
                          onChangePasswordData(
                            "currentPassword",
                            currentPassword
                          )
                        }
                        type={showPassword1 ? "text" : "password"}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword1(!showPassword1)}
                                edge="end"
                              >
                                <Iconify
                                  icon={
                                    showPassword1
                                      ? "eva:eye-fill"
                                      : "eva:eye-off-fill"
                                  }
                                />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <ValidationError
                        formErrors={passwordErrors.currentPassword}
                      />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                      <TextField
                        size="small"
                        style={{ width: "100%" }}
                        label="New Password *"
                        placeholder="New Password *"
                        value={passwordData.newPassword}
                        onChange={(newPassword) =>
                          onChangePasswordData("newPassword", newPassword)
                        }
                        type={showPassword2 ? "text" : "password"}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword2(!showPassword2)}
                                edge="end"
                              >
                                <Iconify
                                  icon={
                                    showPassword2
                                      ? "eva:eye-fill"
                                      : "eva:eye-off-fill"
                                  }
                                />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <ValidationError
                        formErrors={passwordErrors.newPassword}
                      />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                      <TexBoxWithLabel
                        size="small"
                        placeholder="Confirm New Password"
                        multiline={true}
                        label="Confirm New Password *"
                        value={passwordData.confirmPassword}
                        onChange={(confirmPassword) =>
                          onChangePasswordData(
                            "confirmPassword",
                            confirmPassword
                          )
                        }
                      />
                      <ValidationError
                        formErrors={passwordErrors.confirmPassword}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <div style={{ padding: "0 18px 0 0", marginBottom: "15px" }}>
                    <Button
                      variant="contained"
                      onClick={handleClose}
                      style={{
                        backgroundColor: Color.primary,
                        marginRight: "10px",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleChangePassword}
                      variant="contained"
                      style={{ backgroundColor: Color.primary }}
                    >
                      Change Password
                    </Button>
                  </div>
                </DialogActions>
              </Dialog>
            )}
            <LoadingButton
              style={{
                backgroundColor: Color.primary,
                width: "100%",
                marginTop: "40px",
              }}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              onClick={handleClick}
            >
              Save
            </LoadingButton>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
